import { arrow, autoUpdate, FloatingFocusManager, FloatingPortal, offset, shift, useClick, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { mdiCreation, mdiFaceAgent, mdiGhost, mdiSchoolOutline, mdiServerNetwork } from '@mdi/js';
import Icon from '@mdi/react';
import { Close, ExpandMore, HelpOutlineRounded, List, Logout, Menu } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useContext, useEffect, useRef, useState } from 'react';
import SystemData from '../../lib/systemData';
import { GlobalSearchBar } from '../../pages/AdminPublisherList/GlobalSearchBar';
import { TitleContext } from '../../providers/TitleProvider/TitleProvider';
import { stores } from '../../stores';
import { useSidebarStore } from '../../stores/SidebarStore';
import { CurrentUserAvatar } from '../Avatar';
import { IconButton } from '../Button/IconButton';
import { stopGhosting } from '../GhostingHeader/GhostingHeader';
import { Link } from '../Link/Link';
import { SmartHelpContent, SmartHelpProps } from '../Smarts/SmartHelp';
import { queryFn as smartHelpQueryFn, queryKey as smartHelpQueryKey, usePreloadSmartHelp } from '../Smarts/SmartHelpQuery';
import { Tooltip } from '../Tooltip';
import ActiveUsers from './ActiveUsers';
import { CreateMenu } from './CreateMenu';
import { useTopBarStore } from './TopBarStore';
import classes from '../../api/classes';
import { TopBarContext, TopBarLocationState } from './TopBarProvider';
import Base from '../../layouts/Base';
import { Dialog } from '../Dialog';
import { ManageAccount } from '../ManageAccount/ManageAccount';

const { TagData } = classes;

export function TopBarContent() {
	const smartHelp = useTopBarStore((state) => state.smartHelp);
	const { title } = useContext(TitleContext);
	const { mobileOpen, setMobileOpen } = useSidebarStore();
	const {
		topBarLogoUrl,
		topBarLogoStyle,
	} = SystemData.genericData.systemSettings;

	return (
		<div
			className={clsx(
				'flex w-full h-(--topbar-height) items-center flex-row gap-2',
				'bg-bg-static-surface_default border-b border-borders-primary',
			)}
		>
			{/* Logo (sidebar width) */}
			<div
				className="flex flex-shrink-0 items-center justify-center overflow-hidden basis-(--sidebar-lg)"
			>
				{/* Hamburger menu */}
				<IconButton className="block sm:hidden ml-2" active={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)} icon={[<Menu />, <Close />]} />
				{/* Logo */}
				<Link unstyled href="/">
					<img
						src={topBarLogoUrl}
						className="w-full h-full animate-[fade-in_150ms] object-contain"
						style={{
							...topBarLogoStyle,
						}}
						alt="Relevant Logo"
					/>
				</Link>
			</div>
			<div className="flex flex-row justify-end w-full px-6 gap-6">
				<div className="flex flex-grow items-center justify-end gap-5">
					{/* Search Bar */}
					<GlobalSearchBar className="max-sm:hidden" searchBarProps={{ className: 'h-[32px]', shortcut: '$mod+K' }} placeholder="Search for reports, publishers, sites, placements..." />
					{/* "+" menu */}
					<CreateMenu />
					{/* Online users list */}
					<ActiveUsers max={5} />
					{/* Notification Center */}
					{/* <IconButton badge={3} icon={[<NotificationsOutlined />, <Notifications />]} /> */}
					{/* Help Menu with dynamic SmartHelp based on page title */}
					<HelpMenu smartHelp={smartHelp ?? { query: title, question: `Describe the general usage of this page: ${title}` }} />
					{/* User menu */}
					<UserProfileMenu />
				</div>
			</div>
		</div>
	);
}

function PrebidServerVersions() {
	type VersionInfo = { version?: string; revision?: string };

	const [version, setVersion] = useState<VersionInfo | null>(null);

	const styles = {
		container: "px-6 py-4 flex items-center gap-4",
		badge: "font-mono px-2 py-1 rounded text-xs",
		badgeUnknown: "bg-bg-tertiary text-text-tertiary",
		badgeSuccess: "bg-bg-static-success/10 text-text-success_green-bg",
	};


	const getMainVersion = (v?: string) => {
		if (!v || ['unknown', 'not-set'].includes(v)) return null;
		return v.split('-')[0];
	}

	const VersionBadge = ({ version, isLoading }: { version?: string; isLoading: boolean }) => {
		if (isLoading) {
			return <span className="text-text-tertiary text-sm">loading...</span>;
		}

		const mainVersion = getMainVersion(version);

		const badge = (
			<span className={`${styles.badge} ${!mainVersion ? styles.badgeUnknown : styles.badgeSuccess}`}>
				{mainVersion || version || 'Error'}
			</span>
		);

		if (mainVersion) {
			return <Tooltip title={version}>{badge}</Tooltip>;
		}
		return badge;
	};

	const Header = () => (
		<div className="flex items-center gap-4">
			<span className="size-6">
				<Icon path={mdiServerNetwork} />
			</span>
			<span className="text-text-primary">Prebid Server Version</span>
		</div>
	);

	useEffect(() => {
		if (version !== null) return;
		(async () => {
			try {
				const tag = await TagData.getTagData({ dataId: { type: 'programmaticMasterTag' } });
				const url = tag.data.pbsUrl.value;
				if (!url) {
					setVersion({});
					return;
				}
				const response = await fetch(`${url}/version`);
				const data = await response.json();
				setVersion(data);
			} catch (error) {
				console.error('Failed to fetch PBS version:', error);
				setVersion({});
			}
		})();
	}, [version]);

	const isLoading = version === null;
	const displayVersion = version?.version;

	return (
		<div className={styles.container}>
			<Header />
			<span className="ml-auto">
				<VersionBadge isLoading={isLoading} version={displayVersion} />
			</span>
		</div>
	);
}

export function HelpMenu({ smartHelp }: { smartHelp: SmartHelpProps }) {
	const queryClient = useQueryClient();
	const isOpen = useTopBarStore((state) => state.smartHelpOpen);
	const setIsOpen = useTopBarStore((state) => state.setSmartHelpOpen);
	const preloadSmartHelp = usePreloadSmartHelp(smartHelp);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		middleware: [offset({ mainAxis: 20 }), shift(), offset({ crossAxis: -10 })],
		onOpenChange: setIsOpen,
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role,
	]);

	const links: {
		label: string;
		icon: React.ReactNode;
		href?: string;
		to?: string;
		customRender?: () => React.ReactNode;
	}[] = [
		{
			label: 'Relevant Yield Documentation',
			icon: <Icon path={mdiSchoolOutline} />,
			href: 'https://help.relevant-digital.com/knowledge',
		},
		{
			label: 'Release Notes',
			icon: <Icon path={mdiCreation} />,
			href: 'https://help.relevant-digital.com/knowledge/release-and-feature-logs',
		},
		{
			label: 'Contact Support',
			icon: <Icon path={mdiFaceAgent} />,
			href: 'https://help.relevant-digital.com/knowledge/kb-tickets/new',
		},
	];

	const isAdmin = stores.identity.isAdministrator();
	if (isAdmin) {
		links.push({
			label: 'Prebid Server Version',
			icon: <Icon path={mdiServerNetwork} />,
			customRender: () => <PrebidServerVersions />,
		});
	}

	const handleHover = () => {
		preloadSmartHelp();
	};

	return (
		<>
			<IconButton
				ref={refs.setReference}
				icon={<HelpOutlineRounded />}
				{...getReferenceProps()}
				onMouseEnter={handleHover}
			/>
			{isOpen && (
				<FloatingPortal>
					<FloatingFocusManager context={context}>
						<div
							ref={refs.setFloating}
							style={floatingStyles}
							{...getFloatingProps()}
							className="flex w-[450px] flex-col overflow-clip rounded-lg border border-borders-primary bg-bg-static-surface_default shadow-md *:border-borders-primary *:not-last:border-b z-(--z-drawers)"
						>
							{links.map((link) => (
								link.customRender ? (
									<div key={link.label}>{link.customRender()}</div>
								) : (
									<Link
										unstyled
										key={link.label}
										href={link.href}
										onClick={() => { setIsOpen(false); }}
										className="flex items-center px-6 py-4 text-text-primary hover:bg-bg-interactive-light-bg-active_hover"
									>
										<div className="flex items-center gap-4">
											<span className="size-6">{link.icon}</span>
											{link.label}
										</div>
									</Link>
								)
							))}
							<div className="relative p-4">
								<SmartHelpContent
									{...smartHelp}
									onClose={() => setIsOpen(false)}
								/>
							</div>
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			)}
		</>
	);
}

export function UserProfileMenu() {
	const [isOpen, setIsOpen] = useState(false);
	const arrowRef = useRef(null);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		middleware: [offset({ mainAxis: 24 }), shift(), offset({ crossAxis: -10 }), arrow({ element: arrowRef })],
		onOpenChange: setIsOpen,
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role,
	]);

	const user = stores.identity.user();

	const handleLogout = () => {
		if (stores.identity.isGhosting()) {
			stopGhosting();
		} else {
			stores.identity.logout();
		}
	};

	const handleManageAccount = () => {
		setIsOpen(false);
		Base.renderGlobal((close) => (
			<Dialog
				open
				barebones
				text={<ManageAccount onClose={close} />}
				onClose={close}
			/>
		));
	};

	if (!user) {
		return null; // Proably logged out
	}

	return (
		<>
			<button
				className="flex items-center gap-1 rounded-full bg-grey-50 p-1"
				ref={refs.setReference}
				{...getReferenceProps()}
			>
				{stores.identity.isGhosting() && (
					<Tooltip title="Ghosting Mode">
						<Icon path={mdiGhost} className="size-6 text-fg-secondary ml-1" />
					</Tooltip>
				)}
				<CurrentUserAvatar />
				<ExpandMore className="text-grey-800 w-[18px] h-[18px]" />
			</button>
			{isOpen && (
				<FloatingPortal>
					<FloatingFocusManager context={context}>
						<div
							ref={refs.setFloating}
							style={floatingStyles}
							{...getFloatingProps()}
							className="flex w-[350px] flex-col overflow-clip rounded-lg border border-borders-primary bg-bg-static-surface_default shadow-md"
						>
							<div className="flex gap-4 items-center p-6">
								<CurrentUserAvatar className="size-10 shrink-0" />
								<div className="flex flex-col min-w-0">
									<span className="text-grey-800">{user.fullname}</span>
									<span className="text-grey-250 truncate">{user.email}</span>
								</div>
							</div>
							<div className="border-t border-borders-primary" />
							<Link unstyled onClick={handleManageAccount} className="flex gap-4 items-center px-6 py-4 hover:bg-bg-interactive-light-bg-active_hover text-text-primary">
								<List /> Manage Account
							</Link>
							<div className="border-t border-borders-primary" />
							<Link unstyled onClick={handleLogout} className="flex gap-4 items-center px-6 py-4 hover:bg-bg-interactive-light-bg-active_hover text-text-primary">
								<Logout /> Log out
							</Link>
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			)}
		</>
	);
}

export default TopBarContent;
